import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import SignaturePad from './SignaturePad';

const SignaturePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [inputCode, setInputCode] = useState('');
  const [signatureData, setSignatureData] = useState(null);
  const [step, setStep] = useState('loading'); // loading, sign, verify, success, error
  const [sendingCode, setSendingCode] = useState(false);

  useEffect(() => {
    // Récupérer l'état sauvegardé si disponible
    const savedState = localStorage.getItem(`signature_${token}`);
    if (savedState) {
      try {
        const { step: savedStep, verificationCode: savedCode, signatureData: savedSignature } = JSON.parse(savedState);
        if (savedStep === 'verify' && savedCode && savedSignature) {
          setStep(savedStep);
          setVerificationCode(savedCode);
          setSignatureData(savedSignature);
        }
      } catch (e) {
        console.error('Erreur chargement état sauvegardé:', e);
      }
    }
    loadSignatureRequest();
  }, [token]);

  const loadSignatureRequest = async () => {
    try {
      setLoading(true);
      
      // Chercher dans toutes les entreprises pour trouver le token
      const signatureRequestDoc = await getDoc(doc(db, 'signatureRequests', token));
      
      if (!signatureRequestDoc.exists()) {
        setError('Lien de signature invalide ou expiré');
        setStep('error');
        return;
      }

      const requestData = signatureRequestDoc.data();
      
      // Vérifier si déjà signé
      if (requestData.signed) {
        setError('Ce document a déjà été signé');
        setStep('error');
        return;
      }

      // Vérifier l'expiration (7 jours)
      const expirationDate = new Date(requestData.createdAt.toDate());
      expirationDate.setDate(expirationDate.getDate() + 7);
      
      if (new Date() > expirationDate) {
        setError('Ce lien de signature a expiré');
        setStep('error');
        return;
      }

      // Charger le document
      const docRef = doc(
        db,
        'companies',
        requestData.companyId,
        requestData.documentType === 'quote' ? 'quotes' : 'invoices',
        requestData.documentId
      );
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        setError('Document introuvable');
        setStep('error');
        return;
      }

      setDocumentData({
        ...docSnap.data(),
        id: docSnap.id,
        companyId: requestData.companyId,
        documentType: requestData.documentType,
        clientEmail: requestData.clientEmail
      });
      
      setStep('sign');
      setLoading(false);
    } catch (err) {
      console.error('Erreur chargement:', err);
      setError('Erreur lors du chargement du document');
      setStep('error');
      setLoading(false);
    }
  };

  const handleSignature = async (signature) => {
    setSignatureData(signature);
    
    // Générer un code à 6 chiffres
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setVerificationCode(code);
    
    // Envoyer le code par email
    setSendingCode(true);
    try {
      const response = await fetch(
        'https://us-central1-facturation-c415d.cloudfunctions.net/sendVerificationCode',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: documentData.clientEmail,
            code: code,
            documentType: documentData.documentType === 'quote' ? 'Devis' : 'Facture',
            documentNumber: documentData.number,
            clientName: documentData.clientName
          })
        }
      );

      if (!response.ok) throw new Error('Erreur envoi email');
      
      // Sauvegarder l'état pour permettre le retour sur la page
      localStorage.setItem(`signature_${token}`, JSON.stringify({
        step: 'verify',
        verificationCode: code,
        signatureData: signature
      }));
      
      setStep('verify');
    } catch (err) {
      console.error('Erreur envoi code:', err);
      alert('Erreur lors de l\'envoi du code de vérification');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (inputCode !== verificationCode) {
      alert('Code incorrect. Veuillez réessayer.');
      return;
    }

    try {
      setLoading(true);

      // Upload de la signature dans Storage
      const timestamp = Date.now();
      const signatureRef = ref(
        storage,
        `signatures/${documentData.companyId}/${documentData.documentType}/${documentData.id}_${timestamp}.png`
      );
      
      await uploadString(signatureRef, signatureData, 'data_url');
      const signatureUrl = await getDownloadURL(signatureRef);

      // Mise à jour du document avec la signature
      const docRef = doc(
        db,
        'companies',
        documentData.companyId,
        documentData.documentType === 'quote' ? 'quotes' : 'invoices',
        documentData.id
      );

      // Mise à jour du document avec la signature ET changement du statut
      await updateDoc(docRef, {
        signed: true,
        signatureUrl: signatureUrl,
        signedAt: new Date(),
        signedBy: documentData.clientName,
        signedEmail: documentData.clientEmail,
        status: 'Signé' // Changer automatiquement le statut
      });

      // Marquer la demande de signature comme complétée
      await updateDoc(doc(db, 'signatureRequests', token), {
        signed: true,
        signedAt: new Date()
      });

      // Nettoyer le localStorage
      localStorage.removeItem(`signature_${token}`);

      // Créer une notification via Cloud Function sécurisée
      try {
        const functions = getFunctions();
        const createNotification = httpsCallable(functions, 'createSignatureNotification');
        await createNotification({
          companyId: documentData.companyId,
          documentType: documentData.documentType,
          documentNumber: documentData.number,
          clientName: documentData.clientName,
          signedBy: clientName,
          signedEmail: clientEmail
        });
      } catch (notifError) {
        console.error('Erreur création notification:', notifError);
        // Ne pas bloquer le processus si la notification échoue
      }

      setStep('success');
    } catch (err) {
      console.error('Erreur sauvegarde signature:', err);
      alert('Erreur lors de la sauvegarde de la signature');
    } finally {
      setLoading(false);
    }
  };

  if (loading && step === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du document...</p>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Erreur</h1>
          <p className="text-gray-600 mb-6">{error}</p>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-green-500 text-6xl mb-4">✓</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Signature validée !</h1>
          <p className="text-gray-600 mb-6">
            Votre signature a été enregistrée avec succès.
            <br />
            Un email de confirmation vous sera envoyé.
          </p>
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-green-800">
              Document : {documentData.documentType === 'quote' ? 'Devis' : 'Facture'} N°{documentData.number}
              <br />
              Date : {new Date().toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Vérification</h1>
          <p className="text-gray-600 mb-6">
            Un code de vérification à 6 chiffres a été envoyé à l'adresse :
            <br />
            <strong>{documentData.clientEmail}</strong>
          </p>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Code de vérification
            </label>
            <input
              type="text"
              maxLength="6"
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value.replace(/\D/g, ''))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-2xl tracking-widest font-mono"
              placeholder="000000"
            />
          </div>

          <button
            onClick={handleVerifyCode}
            disabled={inputCode.length !== 6 || loading}
            className={`w-full py-3 rounded-lg font-medium transition-colors ${
              inputCode.length !== 6 || loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-orange-500 text-white hover:bg-orange-600'
            }`}
          >
            {loading ? 'Vérification...' : 'Valider'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Signature de {documentData.documentType === 'quote' ? 'devis' : 'facture'}
          </h1>
          <p className="text-gray-600">
            Document N°{documentData.number} - {documentData.clientName}
          </p>
        </div>

        {/* Aperçu du document */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Résumé du document</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Client</p>
              <p className="font-medium">{documentData.clientName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Montant total TTC</p>
              <p className="font-medium text-xl text-orange-600">
                {documentData.total?.toFixed(2) || '0.00'} €
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Date</p>
              <p className="font-medium">
                {documentData.date ? new Date(documentData.date).toLocaleDateString('fr-FR') : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Statut</p>
              <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                En attente de signature
              </span>
            </div>
          </div>
        </div>

        <SignaturePad
          onSave={handleSignature}
          onCancel={() => window.history.back()}
          documentType={documentData.documentType}
          documentNumber={documentData.number}
          clientName={documentData.clientName}
        />
      </div>
    </div>
  );
};

export default SignaturePage;
